import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import Login from './Login';
import BedrockAccess from './BedrockAccess';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import remarkGfm from 'remark-gfm';

const API_URL = process.env.REACT_APP_API_URL || '/api';

interface Message {
  id: string;
  role: 'user' | 'bot';
  content: string;
  timestamp: Date;
}

interface QuickAction {
  id: number;
  label: string;
  query: string;
}

const EventBridgeToggle = ({ apiUrl, token }: { apiUrl: string, token: string }) => {
  const [rules, setRules] = React.useState<{name: string, state: string}[]>([]);
  const [loading, setLoading] = React.useState<string | null>(null);

  React.useEffect(() => {
    axios.get(`${apiUrl}/eventbridge/rules`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setRules(r.data.rules)).catch(() => {});
  }, [apiUrl, token]);

  const toggle = async (ruleName: string, currentState: string) => {
    const action = currentState === 'ENABLED' ? 'disable' : 'enable';
    setLoading(ruleName);
    try {
      const r = await axios.post(`${apiUrl}/eventbridge/toggle-rule`,
        { rule_name: ruleName, action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRules(prev => prev.map(rule =>
        rule.name === ruleName ? { ...rule, state: r.data.state } : rule
      ));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="space-y-2">
      {rules.map(rule => (
        <div key={rule.name} className="flex items-center justify-between px-2 py-1.5 rounded-lg glass-card">
          <span className="text-xs text-blue-200/70 truncate max-w-[120px]" title={rule.name}>
            {rule.name.replace(/-/g, ' ')}
          </span>
          <button
            onClick={() => toggle(rule.name, rule.state)}
            disabled={loading === rule.name}
            className={`relative w-9 h-5 rounded-full transition-all duration-300 flex-shrink-0 ${
              rule.state === 'ENABLED'
                ? 'bg-gradient-to-r from-blue-500 to-purple-500'
                : 'bg-gray-600'
            }`}
          >
            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-300 ${
              rule.state === 'ENABLED' ? 'left-4' : 'left-0.5'
            }`} />
          </button>
        </div>
      ))}
    </div>
  );
};

const PARTICLES = Array.from({length: 25}, (_, i) => ({
  id: i,
  size: Math.random() * 3 + 1,
  color: i % 3 === 0 ? '#4f8ef7' : i % 3 === 1 ? '#8b5cf6' : '#06b6d4',
  left: Math.random() * 100,
  duration: Math.random() * 15 + 10,
  delay: Math.random() * 10,
}));

const Particles = () => (
  <>
    {/* Aurora background */}
    <div className="aurora-bg">
      <div className="aurora-orb" />
    </div>
    {/* Rising particles */}
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {PARTICLES.map(p => (
        <div
          key={p.id}
          className="particle"
          style={{
            width: p.size,
            height: p.size,
            background: p.color,
            boxShadow: `0 0 ${p.size * 3}px ${p.color}`,
            left: `${p.left}%`,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </div>
  </>
);

const TypingIndicator = () => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    className="flex items-center gap-3 px-4 py-3"
  >
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
      R
    </div>
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <motion.div
          animate={{ x: [0, 8, 0], rotate: [0, -10, 10, 0] }}
          transition={{ duration: 0.6, repeat: Infinity, ease: 'easeInOut' }}
          className="flex flex-col items-center"
        >
          <div className="relative">
            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-[8px] font-black text-white shadow-lg">
              R
            </div>
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-500/80 text-white text-[6px] font-bold px-1 rounded-sm whitespace-nowrap">
              ReKnew
            </div>
            <motion.div
              animate={{ scaleY: [1, 0.85, 1] }}
              transition={{ duration: 0.3, repeat: Infinity }}
              className="w-4 h-3 bg-gradient-to-b from-blue-500 to-purple-600 rounded-sm mx-auto mt-0.5"
            />
            <div className="flex gap-0.5 justify-center mt-0.5">
              <motion.div
                animate={{ rotate: [20, -20, 20] }}
                transition={{ duration: 0.3, repeat: Infinity }}
                className="w-1 h-2.5 bg-blue-400 rounded-full origin-top"
              />
              <motion.div
                animate={{ rotate: [-20, 20, -20] }}
                transition={{ duration: 0.3, repeat: Infinity }}
                className="w-1 h-2.5 bg-blue-400 rounded-full origin-top"
              />
            </div>
            <motion.div
              animate={{ rotate: [-30, 30, -30] }}
              transition={{ duration: 0.3, repeat: Infinity }}
              className="absolute top-5 -left-1.5 w-1 h-2 bg-purple-400 rounded-full origin-top"
            />
            <motion.div
              animate={{ rotate: [30, -30, 30] }}
              transition={{ duration: 0.3, repeat: Infinity }}
              className="absolute top-5 -right-1.5 w-1 h-2 bg-purple-400 rounded-full origin-top"
            />
          </div>
        </motion.div>
        <div className="glass message-bot rounded-2xl rounded-tl-none px-4 py-3 flex gap-2 items-center">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="typing-dot w-2 h-2 rounded-full bg-blue-400"
              style={{ animationDelay: `${i * 0.2}s` }}
            />
          ))}
        </div>
      </div>
    </div>
  </motion.div>
)


const TokenLimitControl = () => {
  const [limit, setLimit] = React.useState<number | null>(null);
  const [input, setInput] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    axios.get(`${API_URL}/token-limit`).then(r => {
      setLimit(r.data.limit);
      setInput((r.data.limit / 1000000).toString());
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    const newLimit = Math.min(4500000, Math.round(parseFloat(input) * 1000000));
    try {
      await axios.post(`${API_URL}/token-limit`, { limit: newLimit });
      setLimit(newLimit);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  return (
    <div className="mx-3 mb-3 p-3 glass rounded-xl border border-blue-900/30">
      <p className="text-[10px] text-blue-400/70 uppercase tracking-widest mb-2">Daily Token Limit</p>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => setInput(v => Math.max(0.5, parseFloat(v) - 0.5).toString())}
          className="w-6 h-6 rounded-lg bg-blue-900/40 text-blue-400 text-sm font-bold hover:bg-blue-800/40 flex items-center justify-center"
        >−</button>
        <div className="flex-1 flex items-center gap-1">
          <input
            type="number"
            value={input}
            onChange={e => setInput(e.target.value)}
            className="w-full bg-transparent text-center text-white text-xs outline-none"
            step="0.5"
            min="0.5"
          />
          <span className="text-[10px] text-gray-500 whitespace-nowrap">M tokens</span>
        </div>
        <button
          onClick={() => setInput(v => Math.min(4.5, parseFloat(v) + 0.5).toString())}
          className="w-6 h-6 rounded-lg bg-blue-900/40 text-blue-400 text-sm font-bold hover:bg-blue-800/40 flex items-center justify-center"
        >+</button>
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="w-full mt-2 py-1.5 rounded-lg text-[11px] font-semibold transition-all"
        style={{ background: saved ? '#10b981' : '#4f8ef7', color: 'white' }}
      >
        {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Update Limit'}
      </button>
    </div>
  );
};

const CHART_COLORS = ['#4f8ef7','#a78bfa','#34d399','#f97316','#f43f5e','#06b6d4','#eab308','#8b5cf6'];

const ChartRenderer = ({ content }: { content: string }) => {
  const chartMatch = content.match(/```chart\n([\s\S]*?)```/) || content.match(/```chart\r?\n([\s\S]*?)```/);
  const cleanContent = content.replace(/```chart\n[\s\S]*?```/g, '').trim();
  
  let chartData = null;
  if (chartMatch) {
    try {
      const cleaned = chartMatch[1]
        .replace(/"\s+:/g, '":')
        .replace(/,\s*}/g, '}')
        .replace(/,\s*]/g, ']')
        .trim();
      chartData = JSON.parse(cleaned);
    } catch {}
  }

  // Extract suggestions
  const suggMatch = cleanContent.match(/💡 Suggestions?:(.*?)(?=\n\n|$)/s);
  const mainContent = cleanContent.replace(/💡 Suggestions?:[\s\S]*$/,'').trim();
  const suggestions = suggMatch ? suggMatch[1].trim() : null;

  return (
    <div>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          strong: ({ children }) => <strong className="text-blue-300 font-semibold">{children}</strong>,
          li: ({ children }) => <li className="ml-4 list-disc text-gray-300">{children}</li>,
          code: ({ children }) => <code className="bg-blue-900/40 text-cyan-300 px-1 rounded text-xs">{children}</code>,
          p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
          h2: ({ children }) => <h2 className="text-blue-300 font-bold text-sm mt-2 mb-1">{children}</h2>,
          h3: ({ children }) => <h3 className="text-blue-200 font-semibold text-xs mt-1.5 mb-1">{children}</h3>,
          table: ({ children }) => <div className="overflow-x-auto my-2"><table className="text-xs border-collapse w-full">{children}</table></div>,
          th: ({ children }) => <th className="border border-blue-800/50 bg-blue-900/40 px-2 py-1 text-blue-300 text-left">{children}</th>,
          td: ({ children }) => <td className="border border-blue-800/30 px-2 py-1 text-gray-300">{children}</td>,
        }}
      >
        {mainContent}
      </ReactMarkdown>

      {chartData && (() => {
        const total = chartData.data.reduce((s: number, d: any) => s + d.value, 0);
        const threshold = total * 0.03;
        const main = chartData.data.filter((d: any) => d.value >= threshold);
        const others = chartData.data.filter((d: any) => d.value < threshold);
        const othersTotal = others.reduce((s: number, d: any) => s + d.value, 0);
        const finalData = othersTotal > 0 ? [...main, {name: 'Others', value: Number(othersTotal.toFixed(2))}] : main;
        return (
          <div className="mt-3 p-3 glass rounded-xl border border-blue-900/30">
            <p className="text-xs text-blue-400/70 uppercase tracking-widest mb-2">{chartData.title}</p>
            {chartData.type === 'pie' ? (
              <div>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={finalData} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" labelLine={false} label={false} paddingAngle={2}>
                      {finalData.map((_: any, i: number) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="rgba(0,0,0,0.3)" strokeWidth={1} />)}
                    </Pie>
                    <Tooltip formatter={(v: any) => [typeof v === 'number' && v > 1000 ? v.toLocaleString() : '$'+Number(v).toFixed(2), '']} contentStyle={{background:'#0f172a',border:'1px solid #1e3a5f',borderRadius:'8px',color:'#e2e8f0',fontSize:'12px'}} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-2 px-1">
                  {finalData.map((d: any, i: number) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <div style={{width:8,height:8,borderRadius:'50%',background:CHART_COLORS[i % CHART_COLORS.length],flexShrink:0}} />
                      <span style={{color:'#94a3b8',fontSize:'10px'}}>{d.name} <span style={{color:'#e2e8f0',fontWeight:600}}>{((d.value/total)*100).toFixed(1)}%</span></span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData.data}>
                  <XAxis dataKey="name" tick={{fill:'#64748b',fontSize:10}} />
                  <YAxis tick={{fill:'#64748b',fontSize:10}} />
                  <Tooltip contentStyle={{background:'#0f172a',border:'1px solid #1e3a5f',borderRadius:'8px',color:'#94a3b8'}} />
                  <Bar dataKey="value" fill="#4f8ef7" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        );
      })()}

      {suggestions && (
        <div className="mt-3 p-3 rounded-xl border border-yellow-900/30" style={{background:'rgba(234,179,8,0.05)'}}>
          <p className="text-xs text-yellow-400/80 font-semibold mb-1">💡 Suggestions</p>
          <ReactMarkdown
            components={{
              p: ({ children }) => <p className="text-xs text-yellow-200/70 mb-1">{children}</p>,
              li: ({ children }) => <li className="ml-3 list-disc text-xs text-yellow-200/70">{children}</li>,
            }}
          >
            {suggestions}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
};

export default function App() {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [showBedrockPanel, setShowBedrockPanel] = useState(false);
  const userRole = localStorage.getItem('userRole') || '';
  const [userName, setUserName] = useState<string>(localStorage.getItem('userName') || '');

  const handleLogin = (t: string, name: string) => { setToken(t); setUserName(name); };
  const handleLogout = () => { localStorage.clear(); setToken(null); setUserName(''); };


  const [messages, setMessages] = useState<Message[]>([
    {
      id: '0',
      role: 'bot',
      content: "👋 Welcome to **ReKnew Cloud Assistant**! I have read-only access to your AWS infrastructure — costs, resource utilization, Bedrock usage, and team analytics. Sensitive data like database credentials and environment variables are not accessible. What would you like to explore?",
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [quickActions, setQuickActions] = useState<QuickAction[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    axios.get(`${API_URL}/quick-actions`).then(r => setQuickActions(r.data.actions)).catch(() => {});
  }, []);

  const clearChat = () => {
    setMessages([{
      id: '0', role: 'bot',
      content: "👋 Welcome to **ReKnew Cloud Assistant**! I have read-only access to your AWS infrastructure — costs, resource utilization, Bedrock usage, and team analytics. What would you like to explore?",
      timestamp: new Date(),
    }]);
    setSessionId(null);
  };

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;
    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/chat`, { message: text, session_id: sessionId });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), role: 'bot', content: res.data.response, timestamp: new Date() }]);
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      const errText = typeof detail === 'string' ? detail : JSON.stringify(detail) || e.message || 'Unknown error';
      setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), role: 'bot', content: `❌ Error: ${errText}`, timestamp: new Date() }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  if (!token) return <Login onLogin={handleLogin} />;

  return (
    <div className="h-screen flex flex-col bg-reknew-dark relative overflow-hidden">
      <Particles />

      {/* Header */}
      <div className="relative z-10 glass-dark border-b border-blue-900/30 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-blue-400 hover:text-blue-300 transition-colors mr-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center">
            <img src="/reknew-logo.png" alt="ReKnew" style={{ height: '30px', width: 'auto' }} />
          </div>
          <div>
            <h1 className="font-bold text-base gradient-text leading-tight">Cloud Assistant</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 glass px-3 py-1.5 rounded-full">
            <div className="live-dot" />
            <span className="text-xs text-emerald-400">Live</span>
          </div>
          <button onClick={clearChat} className="text-xs text-blue-400/60 hover:text-blue-400 transition-colors glass px-3 py-1.5 rounded-full">
            Clear
          </button>
          <div className="flex items-center gap-2 glass px-3 py-1.5 rounded-full">
            <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{background:"linear-gradient(135deg,#4f8ef7,#a78bfa)"}}>
              {userName.charAt(0).toUpperCase()}
            </div>
            <span className="text-xs text-blue-300">{userName}</span>
          </div>
          <button onClick={handleLogout} className="text-xs text-red-400/60 hover:text-red-400 transition-colors glass px-3 py-1.5 rounded-full">
            Sign Out
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 220, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="glass-dark border-r border-blue-900/30 flex flex-col overflow-hidden flex-shrink-0"
            >
              <div className="p-3 flex-1 overflow-y-auto">
                <p className="text-xs text-blue-400/50 uppercase tracking-widest mb-3 px-1">Quick Actions</p>
                <div className="flex flex-col gap-1.5">
                  {quickActions.map(a => (
                    <motion.button
                      key={a.id}
                      whileHover={{ x: 4, backgroundColor: 'rgba(79,142,247,0.1)' }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => sendMessage(a.query)}
                      disabled={loading}
                      className="text-left text-xs text-blue-200/80 hover:text-blue-200 px-3 py-2.5 rounded-xl border border-transparent hover:border-blue-500/20 transition-all disabled:opacity-40"
                    >
                      {a.label}
                    </motion.button>
                  ))}
                </div>
              </div>
              <TokenLimitControl />
              {userRole === 'admin' && (
                <div className="px-3 pb-2">
                  <motion.button
                    whileHover={{ x: 4, backgroundColor: 'rgba(79,142,247,0.1)' }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setShowBedrockPanel(!showBedrockPanel)}
                    className="w-full text-center text-xs text-blue-400/70 hover:text-blue-300 px-3 py-2.5 rounded-xl border border-transparent hover:border-blue-500/20 transition-all"
                  >
                    {showBedrockPanel ? '← Back to chat' : 'Bedrock access control'}
                  </motion.button>
                </div>
              )}
              <div className="p-3 border-t border-blue-900/20">
                <p className="text-xs text-blue-400/50 uppercase tracking-widest mb-3 px-1">Automation</p>
                <EventBridgeToggle apiUrl={API_URL} token={token} />
              </div>
              <div className="p-3 border-t border-blue-900/20">
                <p className="text-xs text-blue-400/30 text-center">ReKnew Data & AI</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Bedrock Access Panel */}
        {showBedrockPanel && userRole === 'admin' && (
          <div className="flex-1 overflow-y-auto glass-dark">
            <BedrockAccess />
          </div>
        )}
        {/* Chat Area */}
        <div className="flex-1 flex flex-col overflow-hidden" style={{display: showBedrockPanel ? "none" : "flex"}}>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
            <AnimatePresence initial={false}>
              {messages.map(msg => (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className={`flex items-start gap-3 px-2 py-1 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                      : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                  }`}>
                    {msg.role === 'user' ? 'N' : 'R'}
                  </div>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'message-user rounded-tr-none text-blue-100'
                      : 'message-bot rounded-tl-none text-gray-200'
                  }`}>
                    <ChartRenderer content={msg.content} />
                    <p className="text-xs text-gray-600 mt-1">
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            {loading && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="glass-dark border-t border-blue-900/30 p-4">
            <div className="flex gap-3 items-end glass input-wrapper rounded-2xl px-4 py-3 transition-all">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Ask about your AWS infrastructure..."
                disabled={loading}
                rows={1}
                className="flex-1 bg-transparent text-sm text-gray-200 placeholder-gray-600 resize-none outline-none max-h-32 disabled:opacity-50"
                style={{ lineHeight: '1.5' }}
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => sendMessage(input)}
                disabled={loading || !input.trim()}
                className="send-btn w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </motion.button>
            </div>
            <p className="text-center text-xs text-gray-700 mt-2">Enter to send · Shift+Enter for new line</p>
          </div>
        </div>
      </div>
    </div>
  );
}
